import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import {
  useCreateSystem,
  useDeleteSystem,
  useSupportedSystemTypes,
  useSystemsList,
  useUpdateSystem,
} from '@api/hooks/useCrossroads'
import { System } from '@api/types'
import { GraviGrid, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef, GridApi, ICellRendererParams } from 'ag-grid-community'
import { Button, Form, Input, Modal, Select, message } from 'antd'
import React, { useState, useRef } from 'react'

export const SystemsGrid = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSystem, setEditingSystem] = useState<System | null>(null)
  const [form] = Form.useForm()
  const gridRef = useRef<GridApi | null>(null)

  const { data: systems, isLoading, refetch } = useSystemsList()
  const { data: supportedSystemTypes } = useSupportedSystemTypes()
  const createMutation = useCreateSystem()
  const updateMutation = useUpdateSystem()
  const deleteMutation = useDeleteSystem()

  const columnDefs: ColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.5,
      minWidth: 200,
      // Tenant instances keep a copy of the name, which a rename would leave stale.
      editable: false,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 220,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: supportedSystemTypes || [],
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      editable: true,
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 80,
      cellRenderer: (params: ICellRendererParams) =>
        params.value !== false ? '✓' : '✗',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => (
        <Button
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(params.data)}
          danger
          size='small'
        />
      ),
    },
  ]

  const handleUpdate = async (updatedRow: System) => {
    try {
      await updateMutation.mutateAsync(updatedRow)
      message.success('System updated successfully')
    } catch (_error) {
      message.error('Failed to update system')
      refetch()
    }
  }

  const handleDelete = (system: System) => {
    Modal.confirm({
      title: 'Delete System',
      content: `Are you sure you want to delete system ${system.name}?`,
      onOk: () => {
        deleteMutation.mutate(system.id, {
          onSuccess: () => {
            message.success('System deleted successfully')
            refetch()
          },
        })
      },
    })
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      if (editingSystem) {
        updateMutation.mutate(
          { ...editingSystem, ...values },
          {
            onSuccess: () => {
              message.success('System updated successfully')
              setIsModalOpen(false)
              refetch()
            },
          },
        )
      } else {
        createMutation.mutate(values, {
          onSuccess: () => {
            message.success('System created successfully')
            setIsModalOpen(false)
            refetch()
          },
        })
      }
    })
  }

  return (
    <Vertical bg='surface' style={{ height: '100%', width: '100%' }}>
      <GraviGrid
        rowData={systems}
        columnDefs={columnDefs}
        loading={isLoading}
        controlBarProps={{
          title: 'Systems',
          actionButtons: (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingSystem(null)
                form.resetFields()
                setIsModalOpen(true)
              }}
            >
              Add System
            </Button>
          ),
        }}
        updateEP={handleUpdate}
        agPropOverrides={{
          onGridReady: (params) => {
            gridRef.current = params.api
          },
          getRowId: (params) => params.data._id || params.data.id,
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
          },
          animateRows: true,
          singleClickEdit: true,
        }}
      />
      <Modal
        title={editingSystem ? 'Edit System' : 'Add System'}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => setIsModalOpen(false)}
      >
        <Form form={form} layout='vertical'>
          <Form.Item name='name' label='Name' rules={[{ required: true }]}>
            <Input disabled={!!editingSystem} />
          </Form.Item>
          <Form.Item
            name='type'
            label='Type'
            initialValue='Gravitate Supply & Dispatch'
          >
            <Select
              options={supportedSystemTypes?.map((type) => ({
                value: type,
                label: type,
              }))}
            />
          </Form.Item>
          <Form.Item name='description' label='Description'>
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </Vertical>
  )
}
